import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getServer, updateServer, getServerRmmMeta, deployServerRustdesk } from "../api";
import {
  Server, ArrowLeft, Cpu, HardDrive, MemoryStick, Clock, Activity, WifiOff,
  Save, Terminal, MonitorSmartphone, ShieldCheck, Wrench, History, Loader, Download,
} from "lucide-react";
import { tokens, sx, Panel, FullPageSpinner } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import { useAuth } from "../context/AuthContext";
import ServerTerminal from "../components/ServerTerminal";
import RemoteButton from "../components/RemoteButton";

function timeSince(dateStr) {
  if (!dateStr) return "Never";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function fmtDate(d) { return d ? new Date(d).toLocaleString() : "—"; }

const OS_BADGE = {
  linux:   { label:"Linux",   color:"#22d3ee", icon:Terminal },
  windows: { label:"Windows", color:"#a78bfa", icon:MonitorSmartphone },
  bsd:     { label:"BSD",     color:"#f87171", icon:Server },
  macos:   { label:"macOS",   color:"#cbd5e1", icon:Server },
};

// Big metric ring/tile
function Metric({ label, value, sub, icon: Icon }) {
  const v = value == null || isNaN(value) ? null : Math.round(value);
  const color = v == null ? "var(--t-text-faint)" : v >= 90 ? "#f87171" : v >= 75 ? "#fbbf24" : "#34d399";
  return (
    <div style={{ background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)", borderRadius:tokens.radius.md, padding:"14px 16px", flex:1, minWidth:140 }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}>
        {Icon && <Icon size={12}/>}{label}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color, lineHeight:1, letterSpacing:-1 }}>{v == null ? "—" : `${v}%`}</div>
      <div style={{ height:5, background:"rgba(148,163,184,0.12)", borderRadius:99, overflow:"hidden", marginTop:10 }}>
        <div style={{ height:"100%", width:`${v || 0}%`, background:color, borderRadius:99, transition:"width 0.3s" }}/>
      </div>
      {sub && <div style={{ fontSize:10, color:"var(--t-text-deep)", marginTop:6 }}>{sub}</div>}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", gap:12, padding:"7px 0", borderBottom:"1px solid var(--t-border-soft)" }}>
      <span style={{ fontSize:12, color:"var(--t-text-faint)" }}>{label}</span>
      <span style={{ fontSize:12, color:"var(--t-text-primary)", fontWeight:500, textAlign:"right", wordBreak:"break-word" }}>{value || "—"}</span>
    </div>
  );
}

// Windows remote-desktop control. If RustDesk is set up → the connect button.
// Otherwise an admin can force deployment and watch it light up (the fleet cron
// also deploys within 5 min). Surfaces the precise reason when it can't run.
function WindowsRemote({ server, onRefresh }) {
  const [deploying, setDeploying] = useState(false);
  const [msg, setMsg]   = useState("");
  const [err, setErr]   = useState("");

  // While deploying, poll the parent faster so the button lights up promptly.
  useEffect(() => {
    if (!deploying || server.rustdesk_id) return;
    const t = setInterval(() => onRefresh?.(), 8000);
    return () => clearInterval(t);
  }, [deploying, server.rustdesk_id, onRefresh]);

  // Once the ID lands, stop the "deploying" state.
  useEffect(() => { if (server.rustdesk_id && deploying) setDeploying(false); }, [server.rustdesk_id, deploying]);

  if (server.rustdesk_id) {
    return (
      <RemoteButton
        mode="rustdesk"
        deviceId={server.id}
        deviceName={server.device_name}
        rustdeskId={server.rustdesk_id}
        label="Remote Desktop (RustDesk)"
      />
    );
  }

  const deploy = async () => {
    setErr(""); setMsg("");
    try {
      const r = await deployServerRustdesk(server.id);
      setMsg(r.data?.message || "Deploying RustDesk…");
      if (r.data?.pending || r.data?.already) setDeploying(true);
      onRefresh?.();
    } catch (e) {
      setErr(e.response?.data?.error || "Failed to start RustDesk deployment.");
    }
  };

  return (
    <div>
      <button
        onClick={deploy}
        disabled={deploying}
        style={{
          display:"inline-flex", alignItems:"center", gap:8, padding:"9px 16px", borderRadius:10,
          fontSize:13, fontWeight:700, cursor: deploying ? "default" : "pointer",
          border:"1px solid rgba(167,139,250,0.4)", background:"rgba(167,139,250,0.12)", color:"#c4b5fd",
        }}
      >
        {deploying ? <Loader size={15} style={{ animation:"spin 1s linear infinite" }}/> : <Download size={15}/>}
        {deploying ? "Deploying RustDesk…" : "Set up Remote Desktop"}
      </button>
      {msg && <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:8, lineHeight:1.5 }}>{msg}</div>}
      {err && <div style={{ fontSize:11, color:"#fca5a5", marginTop:8, lineHeight:1.5 }}>{err}</div>}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

export default function Server360() {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ server_roles: [], os_families: [] });
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const { agent } = useAuth();
  const isAdmin = agent?.role === "admin";
  const [showTerminal, setShowTerminal] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await getServer(serverId);
      setServer(r.data);
      setForm({
        os_family:   r.data.os_family || "",
        server_role: r.data.server_role || "",
        ssh_port:    r.data.ssh_port || "",
        ssh_user:    r.data.ssh_user || "",
        monitored:   r.data.monitored !== false,
        server_notes: r.data.server_notes || "",
      });
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [serverId]);

  useEffect(() => { load(); getServerRmmMeta().then(r => setMeta(r.data)).catch(()=>{}); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await updateServer(serverId, {
        ...form,
        ssh_port: form.ssh_port ? parseInt(form.ssh_port, 10) : null,
      });
      setSavedAt(new Date());
      load(true);
    } catch (e) { console.error(e); alert("Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding:40 }}><FullPageSpinner/></div>;
  if (!server) return (
    <div style={{ padding:40, textAlign:"center", color:tokens.text.faint }}>
      Server not found. <button onClick={() => navigate("/server-rmm")} style={{ ...sx.buttonGhost, marginTop:12 }}>Back to Server RMM</button>
    </div>
  );

  const name = server.device_name || server.computer_name || "Unknown";
  const os = OS_BADGE[server.os_family];
  const online = server.status === "online" || (server.last_seen && Date.now() - new Date(server.last_seen).getTime() < 5 * 60_000);
  const statusColor = online ? "#10b981" : "#8d99ae";
  const cpu  = server.cpu_percent ?? server.cpu_load_percent;
  const ram  = server.ram_percent ?? server.ram_used_percent;
  const disk = server.disk_used_percent;
  const up   = server.uptime_hours != null ? (Number(server.uptime_hours) < 24 ? `${Math.round(Number(server.uptime_hours))}h` : `${Math.floor(Number(server.uptime_hours)/24)}d`) : null;

  const labelStyle = { fontSize:10, fontWeight:700, color:"var(--t-text-label)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:5, display:"block" };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1200 }}>
      {/* Back */}
      <button onClick={() => navigate("/server-rmm")} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"none", border:"none", color:tokens.text.faint, fontSize:13, cursor:"pointer", marginBottom:14, fontFamily:"inherit" }}>
        <ArrowLeft size={15}/> Back to Server RMM
      </button>

      {/* Header */}
      <div style={{ display:"flex", gap:16, alignItems:"flex-start", marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ width:56, height:56, borderRadius:14, flexShrink:0, position:"relative", overflow:"hidden",
          background:"linear-gradient(135deg, rgba(79,111,255,0.2), rgba(123,97,255,0.2))", border:"1px solid rgba(79,111,255,0.3)",
          display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 22px rgba(79,111,255,0.22)" }}>
          <Server size={26} color="#22d3ee" style={{ filter:"drop-shadow(0 0 7px rgba(0,212,255,0.5))" }}/>
        </div>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
            <h1 style={{ fontSize:24, fontWeight:800, color:tokens.text.primary, letterSpacing:"-0.5px", margin:0 }}>{name}</h1>
            <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.04em", padding:"4px 10px", borderRadius:99, background: online ? "#052e16" : "#1f2937", color:statusColor }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:statusColor, boxShadow:`0 0 6px ${statusColor}` }}/>{online ? "Online" : "Offline"}
            </span>
            {os && <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:600, color:os.color }}><os.icon size={13}/>{os.label}</span>}
          </div>
          <div style={{ fontSize:13, color:tokens.text.faint, marginTop:5, display:"flex", gap:14, flexWrap:"wrap" }}>
            {server.client_name && <span style={{ color:"#f59e0b" }}>{server.client_name}</span>}
            {server.server_role && <span>{server.server_role}</span>}
            <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>{online ? <Activity size={12}/> : <WifiOff size={12}/>} {timeSince(server.last_seen)}</span>
            {up && <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}><Clock size={12}/> up {up}</span>}
            {server.agent_version && <span style={{ fontFamily:"monospace", color:tokens.text.deep }}>agent v{server.agent_version}</span>}
          </div>
        </div>
      </div>

      {/* Vitals */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
        <Metric label="CPU"  value={cpu}  icon={Cpu}/>
        <Metric label="Memory" value={ram} icon={MemoryStick} sub={server.health_ram_used_gb != null && server.health_ram_total_gb != null ? `${Number(server.health_ram_used_gb).toFixed(1)} / ${Number(server.health_ram_total_gb).toFixed(0)} GB` : null}/>
        <Metric label="Disk" value={disk} icon={HardDrive} sub={server.disk_used_gb != null && server.disk_total_gb != null ? `${Number(server.disk_used_gb).toFixed(0)} / ${Number(server.disk_total_gb).toFixed(0)} GB` : null}/>
        <div style={{ background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)", borderRadius:tokens.radius.md, padding:"14px 16px", flex:1, minWidth:140 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:8 }}><ShieldCheck size={12}/> Health</div>
          <div style={{ fontSize:26, fontWeight:800, color: server.health_score >= 80 ? "#34d399" : server.health_score >= 50 ? "#fbbf24" : "#f87171", lineHeight:1, letterSpacing:-1 }}>
            {server.health_score != null ? server.health_score : "—"}
          </div>
          <div style={{ fontSize:10, color:"var(--t-text-deep)", marginTop:10 }}>{server.health_checked_at ? `checked ${timeSince(server.health_checked_at)}` : "no data"}</div>
        </div>
      </div>

      {/* Two-column: info + settings, then logs */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16, marginBottom:16 }}>
        {/* System info */}
        <Panel title="System Information" bodyStyle={{ padding:"4px 16px 12px" }}>
          <InfoRow label="Hostname" value={server.computer_name || server.device_name}/>
          <InfoRow label="Operating System" value={[server.os, server.os_version].filter(Boolean).join(" ")}/>
          <InfoRow label="Manufacturer" value={server.manufacturer}/>
          <InfoRow label="Model" value={server.model}/>
          <InfoRow label="Last Boot" value={up ? `${up} ago` : null}/>
          <InfoRow label="Last Check-in" value={fmtDate(server.last_seen)}/>
          <InfoRow label="Owner (staff)" value={server.agent_name}/>
          <InfoRow label="Hardware ID" value={server.hardware_id}/>
        </Panel>

        {/* Server settings (editable) */}
        <Panel title="Server Configuration" bodyStyle={{ padding:16 }}
          action={savedAt && <span style={{ fontSize:10, color:"#34d399" }}>Saved {savedAt.toLocaleTimeString()}</span>}>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div style={{ display:"flex", gap:10 }}>
              <div style={{ flex:1 }}>
                <label style={labelStyle}>OS Family</label>
                <select value={form.os_family} onChange={e => setForm(f => ({...f, os_family:e.target.value}))} style={{ ...sx.input, cursor:"pointer" }}>
                  <option value="">Auto</option>
                  {(meta.os_families || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ flex:1 }}>
                <label style={labelStyle}>Server Role</label>
                <select value={form.server_role} onChange={e => setForm(f => ({...f, server_role:e.target.value}))} style={{ ...sx.input, cursor:"pointer" }}>
                  <option value="">—</option>
                  {(meta.server_roles || []).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <div style={{ flex:1 }}>
                <label style={labelStyle}>SSH Port</label>
                <input value={form.ssh_port} onChange={e => setForm(f => ({...f, ssh_port:e.target.value}))} placeholder="22" style={sx.input}/>
              </div>
              <div style={{ flex:1 }}>
                <label style={labelStyle}>SSH User</label>
                <input value={form.ssh_user} onChange={e => setForm(f => ({...f, ssh_user:e.target.value}))} placeholder="root / admin" style={sx.input}/>
              </div>
            </div>
            <div>
              <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:"var(--t-text-body)", cursor:"pointer" }}>
                <input type="checkbox" checked={form.monitored} onChange={e => setForm(f => ({...f, monitored:e.target.checked}))}/>
                Monitoring enabled (alerts + health scoring)
              </label>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea value={form.server_notes} onChange={e => setForm(f => ({...f, server_notes:e.target.value}))} rows={2} style={{ ...sx.input, resize:"vertical" }}/>
            </div>
            <button onClick={save} disabled={saving} style={{ ...sx.buttonPrimary, alignSelf:"flex-start" }}>
              <Save size={14}/> {saving ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </Panel>
      </div>

      {/* Remote access + maintenance */}
      <Panel title="Remote Access & Maintenance" style={{ marginBottom:16 }} bodyStyle={{ padding:16 }}>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:240, padding:14, borderRadius:tokens.radius.md, background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:700, fontSize:13, color:"var(--t-text-primary)", marginBottom:6 }}>
              {server.os_family === "windows" ? <MonitorSmartphone size={15} color="#a78bfa"/> : <Terminal size={15} color="#22d3ee"/>}
              {server.os_family === "windows" ? "Remote Desktop" : "SSH Terminal"}
            </div>
            <div style={{ fontSize:12, color:"var(--t-text-faint)", lineHeight:1.5, marginBottom:12 }}>
              {server.os_family === "windows"
                ? "Connect via RustDesk unattended remote desktop."
                : "Open an in-browser SSH terminal brokered securely through the agent (no inbound ports)."}
            </div>
            {!isAdmin ? (
              <div style={{ fontSize:11, color:"var(--t-text-deep)", fontStyle:"italic" }}>
                Remote access is limited to administrators.
              </div>
            ) : server.os_family === "windows" ? (
              <WindowsRemote server={server} onRefresh={() => load(true)} />
            ) : (
              <button
                onClick={() => setShowTerminal(true)}
                disabled={server.status !== "online"}
                title={server.status !== "online" ? "Server agent is offline" : "Open an in-browser SSH terminal"}
                style={{
                  display:"inline-flex", alignItems:"center", gap:8, padding:"9px 16px", borderRadius:10,
                  fontSize:13, fontWeight:700, cursor: server.status === "online" ? "pointer" : "not-allowed",
                  border:`1px solid ${server.status === "online" ? "rgba(34,211,238,0.4)" : "var(--t-border-soft)"}`,
                  background: server.status === "online" ? "rgba(34,211,238,0.12)" : "var(--t-input)",
                  color: server.status === "online" ? "#67e8f9" : "var(--t-text-deep)",
                  boxShadow: server.status === "online" ? "0 0 14px rgba(34,211,238,0.12)" : "none",
                }}
              >
                <Terminal size={15}/> Open SSH Terminal
              </button>
            )}
          </div>
          <div style={{ flex:1, minWidth:240, padding:14, borderRadius:tokens.radius.md, background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:700, fontSize:13, color:"var(--t-text-primary)", marginBottom:6 }}>
              <Wrench size={15} color="#fbbf24"/> Maintenance
            </div>
            <div style={{ fontSize:12, color:"var(--t-text-faint)", lineHeight:1.5 }}>
              Service control, package/patch updates, processes, disk cleanup & reboot — destructive actions gated by approval.
            </div>
            <div style={{ marginTop:10, fontSize:10, fontWeight:700, color:tokens.accent.indigo, textTransform:"uppercase", letterSpacing:"0.05em" }}>Arriving in maintenance phase</div>
          </div>
        </div>
      </Panel>

      {/* Audit trails */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:16 }}>
        <Panel title="Maintenance History" bodyStyle={{ padding:12 }}>
          {(server.maintenance || []).length === 0 ? (
            <div style={{ padding:20, textAlign:"center", color:"var(--t-text-deep)", fontSize:12 }}>No maintenance actions yet.</div>
          ) : server.maintenance.map(m => (
            <div key={m.id} style={{ display:"flex", gap:10, padding:"8px 6px", borderBottom:"1px solid var(--t-border-soft)" }}>
              <History size={13} color={tokens.accent.indigo} style={{ marginTop:2, flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, color:"var(--t-text-primary)", fontWeight:500 }}>{m.action}</div>
                {m.detail && <div style={{ fontSize:11, color:"var(--t-text-faint)" }}>{m.detail}</div>}
                <div style={{ fontSize:10, color:"var(--t-text-deep)", marginTop:2 }}>{m.tech_name || "system"} · {timeSince(m.created_at)}</div>
              </div>
            </div>
          ))}
        </Panel>
        <Panel title="Remote Session Log" bodyStyle={{ padding:12 }}>
          {(server.ssh_sessions || []).length === 0 ? (
            <div style={{ padding:20, textAlign:"center", color:"var(--t-text-deep)", fontSize:12 }}>No remote sessions yet.</div>
          ) : server.ssh_sessions.map(s => {
            const isRd = s.method === "rustdesk";
            return (
              <div key={s.id} style={{ display:"flex", gap:10, padding:"8px 6px", borderBottom:"1px solid var(--t-border-soft)" }}>
                {isRd
                  ? <MonitorSmartphone size={13} color="#a78bfa" style={{ marginTop:2, flexShrink:0 }}/>
                  : <Terminal size={13} color="#22d3ee" style={{ marginTop:2, flexShrink:0 }}/>}
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:"var(--t-text-primary)", fontWeight:500 }}>
                    {s.tech_name || "Unknown tech"} <span style={{ fontSize:10, fontWeight:600, color: isRd ? "#a78bfa" : "#22d3ee" }}>· {isRd ? "Remote Desktop" : "SSH"}</span>
                  </div>
                  <div style={{ fontSize:10, color:"var(--t-text-deep)", marginTop:2 }}>
                    {fmtDate(s.started_at)} · <span style={{ color: s.status === "open" ? "#34d399" : "var(--t-text-faint)" }}>{s.status}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </Panel>
      </div>

      {showTerminal && (
        <ServerTerminal
          serverId={server.id}
          serverName={server.device_name || server.computer_name}
          sshUser={server.ssh_user}
          onClose={() => { setShowTerminal(false); load(true); }}
        />
      )}
    </div>
  );
}
